// Archetype Intelligence Layer
// Behavioral archetype recognition — NOT clinical diagnosis.
// Identifies recurring conversational identities from message patterns and
// tactic sequences. Tone is forensic, observational, behavioral.

import { supabase } from "@/integrations/supabase/client";

export type ArchetypeKey =
  | "deflector"
  | "minimizer"
  | "reverser"
  | "escalator"
  | "avoider"
  | "controller"
  | "emotional_accountant"
  | "interrogator"
  | "future_faker"
  | "boundary_tester";

export interface Archetype {
  key: ArchetypeKey;
  name: string;          // "The Deflector"
  short: string;         // one-line behavioral summary
  tendencies: string[];  // conversational tendencies (3–4)
  phrases: string[];     // common phrases (3–4)
  escalation: string;    // escalation style
  emotionalEffect: string; // what it does to the listener
}

export const ARCHETYPES: Record<ArchetypeKey, Archetype> = {
  deflector: {
    key: "deflector",
    name: "The Deflector",
    short: "Redirects focus away from the original issue.",
    tendencies: [
      "Pivots to unrelated grievances when challenged.",
      "Answers questions with questions.",
      "Reframes specifics into vague abstractions.",
    ],
    phrases: [
      "Why are we always talking about me?",
      "What about when you…",
      "That's not the point.",
    ],
    escalation: "Adds adjacent topics until the original concern dissolves.",
    emotionalEffect: "Listener forgets what they came to say.",
  },
  minimizer: {
    key: "minimizer",
    name: "The Minimizer",
    short: "Reframes emotional concerns as exaggeration.",
    tendencies: [
      "Treats specific incidents as one-off mistakes.",
      "Compares the concern to something 'worse'.",
      "Uses humor to shrink the conversation.",
    ],
    phrases: [
      "You're being dramatic.",
      "It wasn't that big of a deal.",
      "I was just joking.",
    ],
    escalation: "Mockery surfaces if the concern is held firmly.",
    emotionalEffect: "Listener begins to doubt the size of their own reaction.",
  },
  reverser: {
    key: "reverser",
    name: "The Reverser",
    short: "Inverts the conflict so the concern-raiser becomes the accused.",
    tendencies: [
      "Pivots from defense to accusation mid-sentence.",
      "Lists the listener's past failures as rebuttal.",
      "Frames their own behavior as a response to provocation.",
    ],
    phrases: [
      "If you hadn't…",
      "Look what you made me do.",
      "You're the one who started this.",
    ],
    escalation: "Reframes themselves as the wronged party.",
    emotionalEffect: "Listener moves from raising a concern to defending themselves.",
  },
  escalator: {
    key: "escalator",
    name: "The Escalator",
    short: "Increases emotional intensity when control weakens.",
    tendencies: [
      "Raises stakes when challenged.",
      "Uses absolutes — always, never, every time.",
      "Threatens withdrawal or finality.",
    ],
    phrases: [
      "Forget it.",
      "Every single time.",
      "Fine then.",
    ],
    escalation: "Compresses the listener's response space with urgency.",
    emotionalEffect: "Listener feels rushed into resolution before clarity.",
  },
  avoider: {
    key: "avoider",
    name: "The Avoider",
    short: "Withdraws to neutralize accountability.",
    tendencies: [
      "Goes silent under direct questions.",
      "Postpones the conversation indefinitely.",
      "Disappears mid-thread when pressed.",
    ],
    phrases: [
      "I don't want to talk about this.",
      "Can we drop it.",
      "Later.",
    ],
    escalation: "Silence used as pressure.",
    emotionalEffect: "Listener carries the entire weight of the unresolved exchange.",
  },
  controller: {
    key: "controller",
    name: "The Controller",
    short: "Sets the terms of how a conversation may unfold.",
    tendencies: [
      "Dictates when, where and how things are discussed.",
      "Frames disagreement as misbehavior.",
      "Issues conditions instead of requests.",
    ],
    phrases: [
      "We'll talk about this when I say so.",
      "That's not how this works.",
      "I need you to…",
    ],
    escalation: "Frames non-compliance as a personal attack.",
    emotionalEffect: "Listener begins narrating themselves through the other person's expectations.",
  },
  emotional_accountant: {
    key: "emotional_accountant",
    name: "The Emotional Accountant",
    short: "Tracks favors and frames affection as a balance owed.",
    tendencies: [
      "Cites past sacrifices during current disagreements.",
      "Quantifies emotional labor.",
      "Treats kindness as a debt instrument.",
    ],
    phrases: [
      "After everything I've done for you…",
      "I sacrificed so much.",
      "You don't appreciate me.",
    ],
    escalation: "Invoices grow longer when needs are raised.",
    emotionalEffect: "Listener feels indebted before stating their own need.",
  },
  interrogator: {
    key: "interrogator",
    name: "The Interrogator",
    short: "Uses questioning as pressure rather than curiosity.",
    tendencies: [
      "Asks questions designed to corner, not understand.",
      "Demands explanation for ordinary behavior.",
      "Treats answers as evidence to be cross-examined.",
    ],
    phrases: [
      "Why did you really…",
      "Who were you with?",
      "Explain that to me.",
    ],
    escalation: "Question loops tighten until the listener concedes.",
    emotionalEffect: "Listener begins behaving as if every action requires defense.",
  },
  future_faker: {
    key: "future_faker",
    name: "The Future Faker",
    short: "Resolves present friction with promises about later.",
    tendencies: [
      "Offers vague future change instead of present accountability.",
      "Redirects current concerns into hypothetical plans.",
      "Apologizes in the form of intentions.",
    ],
    phrases: [
      "Things will be different.",
      "I'm going to start…",
      "Soon, I promise.",
    ],
    escalation: "Larger promises follow stronger pushback.",
    emotionalEffect: "Listener loses the present concern in the optimism of the future one.",
  },
  boundary_tester: {
    key: "boundary_tester",
    name: "The Boundary Tester",
    short: "Probes limits incrementally to learn what holds.",
    tendencies: [
      "Tests small infractions before larger ones.",
      "Frames violations as misunderstandings.",
      "Renegotiates boundaries that were already settled.",
    ],
    phrases: [
      "I didn't think you'd mind.",
      "It's just this once.",
      "I forgot you cared about that.",
    ],
    escalation: "Small breaches expand until met with consistent friction.",
    emotionalEffect: "Listener spends energy maintaining lines that should already be respected.",
  },
};

// ── Detection ────────────────────────────────────────────────────────────────

interface ArchetypeMatcher {
  archetype: ArchetypeKey;
  rx?: RegExp;
  tactics?: string[]; // tactic_tag substrings
  weight: number;
}

const MATCHERS: ArchetypeMatcher[] = [
  // Deflector
  { archetype: "deflector", rx: /\b(what about (when )?you|that'?s not the point|why are we always talking about me)\b/i, weight: 3 },
  { archetype: "deflector", tactics: ["deflect", "topic-shift", "whataboutism"], weight: 2 },

  // Minimizer
  { archetype: "minimizer", rx: /\b(you'?re being dramatic|wasn'?t that big|i was just joking|can'?t you take a joke|overreact)\b/i, weight: 3 },
  { archetype: "minimizer", tactics: ["minimiz", "dismiss", "trivializ"], weight: 2 },

  // Reverser (DARVO)
  { archetype: "reverser", rx: /\b(if you hadn'?t|look what you (made me|did)|you'?re the one who|this is your fault|you made me)\b/i, weight: 3 },
  { archetype: "reverser", tactics: ["darvo", "reversal", "blame-shift"], weight: 3 },

  // Escalator
  { archetype: "escalator", rx: /\b(forget it|every single time|you always|you never|fine then|whatever)\b/i, weight: 2 },
  { archetype: "escalator", tactics: ["escalat", "ultimatum", "threat"], weight: 3 },

  // Avoider
  { archetype: "avoider", rx: /\b(i don'?t want to talk|can we drop it|not now|later\.?$|we'?re done here)\b/i, weight: 3 },
  { archetype: "avoider", tactics: ["stonewall", "withdraw", "avoid"], weight: 3 },

  // Controller
  { archetype: "controller", rx: /\b(that'?s not how this works|we'?ll talk about this when|i need you to|you will|you'?re not allowed)\b/i, weight: 2 },
  { archetype: "controller", tactics: ["control", "command", "dictate"], weight: 2 },

  // Emotional Accountant
  { archetype: "emotional_accountant", rx: /\b(after everything i'?ve done|i sacrifice|you don'?t appreciate|nobody appreciates|i gave up)\b/i, weight: 3 },
  { archetype: "emotional_accountant", tactics: ["guilt", "obligation", "debt"], weight: 2 },

  // Interrogator
  { archetype: "interrogator", rx: /\b(why did you really|who were you with|explain (that|yourself)|i need an answer)\b/i, weight: 2 },
  { archetype: "interrogator", tactics: ["interrogat", "cross-examin"], weight: 3 },

  // Future Faker
  { archetype: "future_faker", rx: /\b(things will be different|i'?m going to (start|change)|soon,? i promise|next time i)\b/i, weight: 3 },
  { archetype: "future_faker", tactics: ["future-fak", "promise", "hoover"], weight: 3 },

  // Boundary Tester
  { archetype: "boundary_tester", rx: /\b(i didn'?t think you'?d mind|just this once|i forgot you cared|you never said i couldn'?t)\b/i, weight: 3 },
  { archetype: "boundary_tester", tactics: ["boundary-test", "probe", "incremental"], weight: 2 },
];

export interface RehearsalMessage {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

export interface DetectedArchetype {
  archetype: Archetype;
  score: number;         // raw weighted hits
  confidence: number;    // 0-1 (relative to top)
  evidenceTurns: number[];
}

export function detectArchetypes(
  messages: RehearsalMessage[],
  tacticsSequence?: { turn: number; tactic: string }[] | null
): DetectedArchetype[] {
  const tally = new Map<ArchetypeKey, { score: number; turns: Set<number> }>();
  const bump = (k: ArchetypeKey, w: number, turn: number) => {
    const cur = tally.get(k) || { score: 0, turns: new Set<number>() };
    cur.score += w;
    cur.turns.add(turn);
    tally.set(k, cur);
  };

  for (const msg of messages) {
    if (msg.role !== "agent") continue;
    const text = msg.content || "";
    for (const m of MATCHERS) {
      if (m.rx && m.rx.test(text)) bump(m.archetype, m.weight, msg.turn);
      if (m.tactics && msg.tactic_tag) {
        const tag = msg.tactic_tag.toLowerCase();
        if (m.tactics.some((t) => tag.includes(t))) bump(m.archetype, m.weight, msg.turn);
      }
    }
  }

  if (tacticsSequence) {
    for (const t of tacticsSequence) {
      const tag = (t.tactic || "").toLowerCase();
      for (const m of MATCHERS) {
        if (m.tactics && m.tactics.some((s) => tag.includes(s))) bump(m.archetype, m.weight, t.turn);
      }
    }
  }

  const ranked = Array.from(tally.entries())
    .map(([key, v]) => ({ key, score: v.score, turns: Array.from(v.turns).sort((a, b) => a - b) }))
    .filter((r) => r.score >= 2)
    .sort((a, b) => b.score - a.score);

  if (!ranked.length) return [];
  const top = ranked[0].score;

  return ranked.slice(0, 3).map((r) => ({
    archetype: ARCHETYPES[r.key],
    score: r.score,
    confidence: Math.min(1, r.score / Math.max(top, 4)),
    evidenceTurns: r.turns,
  }));
}

// Phrase-only quick detection for ambient/live use during a rehearsal.
// Returns the most recently emerging archetype (if any), or null.
export function detectEmergingArchetype(
  messages: RehearsalMessage[]
): { archetype: Archetype; turn: number } | null {
  const recent = messages.slice(-4).filter((m) => m.role === "agent");
  for (let i = recent.length - 1; i >= 0; i--) {
    const msg = recent[i];
    for (const m of MATCHERS) {
      if (m.rx && m.rx.test(msg.content)) {
        return { archetype: ARCHETYPES[m.archetype], turn: msg.turn };
      }
    }
  }
  return null;
}

// ── Cross-session memory ─────────────────────────────────────────────────────

export interface ArchetypeExposure {
  archetype: Archetype;
  sessions: number;
  lastSeen: string; // ISO
}

export async function fetchArchetypeMemory(
  userId: string,
  limit = 12
): Promise<ArchetypeExposure[]> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, created_at, messages, tactics_sequence")
    .eq("user_id", userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) return [];

  const counts = new Map<ArchetypeKey, { sessions: number; lastSeen: string }>();
  for (const row of data) {
    const msgs = (row.messages as unknown as RehearsalMessage[]) || [];
    const seq = (row.tactics_sequence as unknown as { turn: number; tactic: string }[]) || null;
    const detected = detectArchetypes(msgs, seq);
    const seenInThisSession = new Set<ArchetypeKey>();
    for (const d of detected) {
      if (seenInThisSession.has(d.archetype.key)) continue;
      seenInThisSession.add(d.archetype.key);
      const cur = counts.get(d.archetype.key);
      if (!cur) {
        counts.set(d.archetype.key, { sessions: 1, lastSeen: row.created_at });
      } else {
        cur.sessions += 1;
        if (row.created_at > cur.lastSeen) cur.lastSeen = row.created_at;
      }
    }
  }

  return Array.from(counts.entries())
    .map(([key, v]) => ({ archetype: ARCHETYPES[key], sessions: v.sessions, lastSeen: v.lastSeen }))
    .sort((a, b) => b.sessions - a.sessions);
}

// Single-line ambient memory line for the Practice homepage.
export async function fetchArchetypeMemoryLine(userId: string): Promise<string | null> {
  const exposures = await fetchArchetypeMemory(userId, 10);
  if (!exposures.length) return null;
  const top = exposures[0];
  if (top.sessions < 2) return null;
  return `You frequently rehearse against ${top.archetype.name} patterns.`;
}
